import { useEffect, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { RoleBadge } from '@/components/common/RoleBadge';
import { ROLE_LABELS } from '@/lib/roles';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { fmtDate } from '@/lib/format';
import { KeyRound, Copy, Check, AlertTriangle } from 'lucide-react';

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [role, setRole] = useState('team');
  const [reportsTo, setReportsTo] = useState('');
  const [resetTarget, setResetTarget] = useState(null);     // user we want to reset
  const [resetResult, setResetResult] = useState(null);      // { temp_password, user_email, user_name }
  const [resetting, setResetting] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = () => api.get('/users?include_inactive=true&include_master=true').then(r => setUsers(r.data || [])).catch(e => toast.error(parseErr(e)));
  useEffect(() => { load(); }, []);

  const openAssign = (u) => { setEditing(u); setRole(u.role || 'team'); setReportsTo(u.reports_to || ''); };
  const save = async () => {
    try { await api.post(`/users/${editing.id}/role`, { role, reports_to: reportsTo || null, department: editing.department || null }); toast.success('Role assigned'); setEditing(null); load(); }
    catch (e) { toast.error(parseErr(e)); }
  };
  const setStatus = async (u, status) => {
    try { await api.post(`/users/${u.id}/status`, { status }); toast.success(`Status: ${status}`); load(); }
    catch (e) { toast.error(parseErr(e)); }
  };

  const doReset = async () => {
    if (!resetTarget) return;
    setResetting(true);
    try {
      const r = await api.post(`/users/${resetTarget.id}/reset-password`);
      setResetResult(r.data);
    } catch (e) {
      toast.error(parseErr(e));
      setResetTarget(null);
    } finally {
      setResetting(false);
    }
  };

  const closeResetDialog = () => {
    setResetTarget(null);
    setResetResult(null);
    setCopied(false);
    load();
  };

  const copyPassword = async () => {
    if (!resetResult?.temp_password) return;
    try {
      await navigator.clipboard.writeText(resetResult.temp_password);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copy failed — select the text manually');
    }
  };

  return (
    <AppShell breadcrumbs={[{ label: 'Admin', to: '/admin/users' }, { label: 'Users' }]}>
      <div className="p-6 max-w-[1200px] mx-auto" data-testid="admin-users-page">
        <h1 className="font-heading text-2xl font-semibold">Users</h1>
        <p className="text-[13px] text-[hsl(var(--foreground-muted))]">{users.length} users · approve pending, assign roles, deactivate.</p>
        <div className="mt-4 border rounded-xl bg-[hsl(var(--card))] overflow-x-auto" style={{ borderColor: 'hsl(var(--border))' }}>
          <table className="tbl" data-testid="admin-users-table">
            <thead><tr><th>User</th><th>Role</th><th>Department</th><th>Status</th><th>Joined</th><th></th></tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} data-testid={`admin-user-row-${u.id}`}>
                  <td>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-semibold" style={{ background: u.avatar_color || 'hsl(var(--primary))' }}>{u.name?.split(' ').map(w => w[0]).slice(0, 2).join('')}</div>
                      <div>
                        <div className="font-medium flex items-center gap-1">
                          {u.name}
                          {u.is_master_account && (
                            <span className="text-[9.5px] font-semibold px-1.5 py-[1px] rounded-full text-white"
                                  style={{ background: 'hsl(var(--primary))' }}
                                  title="Master account — protected">MASTER</span>
                          )}
                          {u.force_password_change && (
                            <span className="text-[9.5px] font-semibold px-1.5 py-[1px] rounded-full text-white"
                                  style={{ background: 'hsl(var(--warning))' }}
                                  title="User must change password on next sign-in">RESET PENDING</span>
                          )}
                        </div>
                        <div className="text-[11.5px] font-mono text-[hsl(var(--foreground-muted))]">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>{u.role ? <RoleBadge role={u.role} /> : <span className="chip">Not assigned</span>}</td>
                  <td className="text-[13px]">{u.department || '—'}</td>
                  <td><span className={`chip ${u.status === 'active' ? 'chip-success' : u.status === 'pending' ? 'chip-warning' : 'chip-danger'}`}>{u.status}</span></td>
                  <td className="text-[12px] text-[hsl(var(--foreground-muted))]">{fmtDate(u.created_at)}</td>
                  <td className="text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => openAssign(u)} data-testid={`admin-user-assign-${u.id}`}>Assign role</Button>
                    {!u.is_master_account && (
                      <Button size="sm" variant="ghost" onClick={() => setResetTarget(u)} className="ml-1"
                              data-testid={`admin-user-reset-pw-${u.id}`} title="Reset password (generates a one-time temp)">
                        <KeyRound size={13} className="mr-0.5" />Reset PW
                      </Button>
                    )}
                    {u.status === 'active' && <Button size="sm" variant="ghost" onClick={() => setStatus(u, 'disabled')} className="ml-1" data-testid={`admin-user-disable-${u.id}`}>Disable</Button>}
                    {u.status === 'disabled' && <Button size="sm" variant="ghost" onClick={() => setStatus(u, 'active')} className="ml-1">Enable</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Dialog open={!!editing} onOpenChange={o => !o && setEditing(null)}>
          <DialogContent data-testid="admin-assign-dialog">
            <DialogHeader><DialogTitle>Assign role — {editing?.name}</DialogTitle></DialogHeader>
            <label className="block"><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Role</span>
              <select className="mt-1 w-full h-9 border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }} value={role} onChange={e => setRole(e.target.value)} data-testid="admin-assign-role">
                {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <label className="block"><span className="text-[12px] text-[hsl(var(--foreground-muted))]">Reports to (optional)</span>
              <select className="mt-1 w-full h-9 border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }} value={reportsTo} onChange={e => setReportsTo(e.target.value)}>
                <option value="">— None —</option>
                {users.filter(u => u.id !== editing?.id).map(u => <option key={u.id} value={u.id}>{u.name} · {u.role || 'unassigned'}</option>)}
              </select>
            </label>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={save} data-testid="admin-assign-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset-password dialog — 2 states: confirm, then reveal */}
        <Dialog open={!!resetTarget} onOpenChange={o => !o && closeResetDialog()}>
          <DialogContent data-testid="admin-reset-pw-dialog">
            {!resetResult ? (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <KeyRound size={18} />Reset password for {resetTarget?.name}?
                  </DialogTitle>
                  <DialogDescription>
                    A one-time temporary password will be generated. Share it with{' '}
                    <span className="font-mono">{resetTarget?.email}</span> out-of-band
                    (WhatsApp, in-person). They will be forced to set a new password on
                    their next sign-in.
                  </DialogDescription>
                </DialogHeader>
                <div className="text-[12.5px] p-3 rounded border flex items-start gap-2"
                     style={{ borderColor: 'hsl(48 90% 80%)', background: 'hsl(48 96% 96%)', color: 'hsl(30 60% 25%)' }}>
                  <AlertTriangle size={16} className="mt-0.5 flex-none" />
                  <div>
                    Their current password will stop working immediately. This action is
                    logged in the audit trail.
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={closeResetDialog} disabled={resetting}>Cancel</Button>
                  <Button onClick={doReset} disabled={resetting} data-testid="admin-reset-pw-confirm">
                    {resetting ? 'Generating…' : 'Generate temp password'}
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Check size={18} className="text-[hsl(var(--success))]" />
                    Temp password ready
                  </DialogTitle>
                  <DialogDescription>
                    Share this password with <strong>{resetResult.user_email}</strong> now — this
                    is the only time it will be shown. They will be prompted to set a new
                    permanent password on their next sign-in.
                  </DialogDescription>
                </DialogHeader>
                <div className="p-3 rounded-lg border flex items-center gap-3"
                     style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface-1))' }}
                     data-testid="admin-reset-pw-result">
                  <code className="font-mono text-[16px] tracking-wider flex-1 select-all break-all"
                        data-testid="admin-reset-pw-value">
                    {resetResult.temp_password}
                  </code>
                  <Button size="sm" variant="outline" onClick={copyPassword}
                          data-testid="admin-reset-pw-copy">
                    {copied ? <><Check size={13} className="mr-1" />Copied</> : <><Copy size={13} className="mr-1" />Copy</>}
                  </Button>
                </div>
                <div className="text-[11.5px] text-[hsl(var(--foreground-subtle))]">
                  {resetResult.note}
                </div>
                <DialogFooter>
                  <Button onClick={closeResetDialog} data-testid="admin-reset-pw-done">Done</Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}
